import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@mono/ui-components';
import { IconDownload as Download, IconFileText as FileText, IconArrowsMaximize as Maximize2, IconPresentation as Presentation } from '@tabler/icons-react';
import { useCallback, useRef, useState } from 'react';
import { MermaidDiagram } from './MermaidDiagram';
import { MermaidViewerDialog } from './MermaidViewerDialog';
import { exportMermaidRaster } from './mermaidOfficeExport';
import { useDragPan, usePanZoom } from './useMermaidInteractions';
function parseSvgSize(s) {
    try {
        const vbMatch = s.match(/viewBox="([^"]+)"/);
        if (vbMatch?.[1]) {
            const parts = vbMatch[1].split(/\s+/).map((p)=>Number(p));
            if (parts.length === 4 && parts.every((n)=>Number.isFinite(n))) {
                return {
                    width: Math.max(1, parts[2]),
                    height: Math.max(1, parts[3])
                };
            }
        }
        const wMatch = s.match(/\bwidth="(\d+(?:\.\d+)?)"/);
        const hMatch = s.match(/\bheight="(\d+(?:\.\d+)?)"/);
        if (wMatch && hMatch) {
            return {
                width: Number(wMatch[1]),
                height: Number(hMatch[1])
            };
        }
    } catch (_err) {
    // Parsing failed, return default
    }
    return {
        width: 1200,
        height: 800
    };
}
export function MermaidInline({ code }) {
    const [svg, setSvg] = useState('');
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState(()=>typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'default');
    const [diagramSize, setDiagramSize] = useState(null);
    const viewportRef = useRef(null);
    const diagramContainerRef = useRef(null);
    // Use custom hooks for pan/zoom and drag interactions
    const { scale, setScale, pan, setPan, fitToScreen, resetView } = usePanZoom(diagramSize, viewportRef);
    const { beginDrag, onDrag, endDrag } = useDragPan(pan, setPan);
    // Memoize the onRendered callback to prevent unnecessary re-renders
    const handleRendered = useCallback((s)=>{
        setSvg(s);
        setDiagramSize(parseSvgSize(s));
    }, []);
    const rasterBackground = theme === 'dark' ? '#1a1a1a' : '#ffffff';
    const rasterSize = diagramSize ?? {
        width: 1200,
        height: 800
    };
    const exportRaster = async (format, container)=>{
        const svgElement = container?.querySelector('svg');
        if (!svgElement) return;
        try {
            await exportMermaidRaster(svgElement, format, {
                backgroundColor: rasterBackground,
                size: rasterSize
            });
        } catch  {
        // Same as PNG download: fail closed if the SVG is not capturable.
        }
    };
    const download = async (fmt)=>{
        if (fmt === 'svg') {
            if (!svg) return;
            const blob = new Blob([
                svg
            ], {
                type: 'image/svg+xml'
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'diagram.svg';
            a.click();
            URL.revokeObjectURL(url);
            return;
        }
        await exportRaster(fmt, diagramContainerRef.current);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative group",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "absolute right-2 top-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        size: "icon",
                        variant: "outline",
                        className: "h-8 w-8",
                        onClick: ()=>setOpen(true),
                        "aria-label": "Expand diagram",
                        children: /*#__PURE__*/ _jsx(Maximize2, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "icon",
                        variant: "outline",
                        className: "h-8 w-8",
                        onClick: ()=>download('svg'),
                        "aria-label": "Download SVG",
                        children: /*#__PURE__*/ _jsx(Download, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "icon",
                        variant: "outline",
                        className: "h-8 w-8",
                        onClick: ()=>download('png'),
                        "aria-label": "Download PNG",
                        children: /*#__PURE__*/ _jsx(Download, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "icon",
                        variant: "outline",
                        className: "h-8 w-8",
                        onClick: ()=>download('pptx'),
                        "aria-label": "Download PowerPoint",
                        children: /*#__PURE__*/ _jsx(Presentation, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "icon",
                        variant: "outline",
                        className: "h-8 w-8",
                        onClick: ()=>download('docx'),
                        "aria-label": "Download Word",
                        children: /*#__PURE__*/ _jsx(FileText, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: diagramContainerRef,
                children: /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    "aria-label": "Open diagram",
                    className: "cursor-zoom-in w-full border-0 bg-transparent p-0",
                    onClick: ()=>setOpen(true),
                    children: /*#__PURE__*/ _jsx(MermaidDiagram, {
                        code: code,
                        theme: theme,
                        onRendered: handleRendered
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(Dialog, {
                open: open,
                onOpenChange: (isOpen)=>{
                    setOpen(isOpen);
                    if (isOpen) {
                        setTimeout(()=>fitToScreen(), 30);
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(DialogTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx("span", {
                            hidden: true
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DialogContent, {
                        className: "w-[95vw] max-w-[1600px] h-[85vh]",
                        children: [
                            /*#__PURE__*/ _jsx(DialogHeader, {
                                children: /*#__PURE__*/ _jsx(DialogTitle, {
                                    children: "Diagram"
                                })
                            }),
                            /*#__PURE__*/ _jsx(MermaidViewerDialog, {
                                code: code,
                                theme: theme,
                                setTheme: setTheme,
                                scale: scale,
                                setScale: setScale,
                                pan: pan,
                                setPan: setPan,
                                diagramSize: diagramSize,
                                viewportRef: viewportRef,
                                fitToScreen: fitToScreen,
                                resetView: resetView,
                                download: download,
                                beginDrag: beginDrag,
                                onDrag: onDrag,
                                endDrag: endDrag
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export default MermaidInline;
